// @flow strict

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {formatDateTime} from './utils';

import type {SchedulerEvent} from 'src/types/scheduler';

import {
  Card,
  ClickableCard,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  SubTitleLarge,
  BodySmall,
  BodySmallBold,
  SubTitleMedium,
  BodyMediumBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {RadioButton} from '@spaced-out/ui-design-system/lib/components/RadioButton';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';
import {Banner} from '@spaced-out/ui-design-system/lib/components/Banner';

//$FlowFixMe[nonstrict-import]
import * as api from 'src/utils/api-no-store';

import css from './booking.css';


type IProps = {
  eventId: string,
  originallyScheduledDate: Date,
  event: SchedulerEvent | null,
  actionType: 'reschedule' | 'cancel',
};

const ActionPrompt = ({
  eventId,
  originallyScheduledDate,
  event,
  actionType,
}: IProps): React.Node => {
  const {t, makeKey} = useLabelTranslation();
  const [reason, setReason] = React.useState<string>('');
  const [identity, setIdentity] = React.useState<'HOST' | 'CANDIDATE' | null>(
    null,
  );
  const [requested, setRequested] = React.useState<boolean>(false);
  const [isError, setIsError] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [errorText, setErrorText] = React.useState<string>('');

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post(`scheduler/event/${eventId}/send-link`, {
        participant_type: identity,
        link_type: actionType,
        reason: actionType === 'reschedule' ? reason : '',
      });
    } catch (error) {
      const {responseBody} = error || {};
      const {errors, data} = responseBody || {};
      const [firstError] = errors || [];
      if (
        ['EventLinkAlreadyRequested', 'EventInThePast'].includes(firstError)
      ) {
        let errorText =
          firstError === 'EventLinkAlreadyRequested'
            ? 'An email requesting reschedule has already been sent to the guest.'
            : 'Event is in the past';

        if (data?.ttl) {
          const ttl = data.ttl;
          const hours = Math.floor(ttl / 3600);
          const minutes = Math.floor((ttl % 3600) / 60);
          const timeString =
            hours > 0
              ? `${hours} ${hours === 1 ? 'hour' : 'hours'}`
              : `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
          errorText += ` You can request again after ${timeString}.`;
        }

        setErrorText(errorText);
      }
      setIsError(true);
    } finally {
      setRequested(true);
      setLoading(false);
    }
  };

  const changeIdentity = (value) => {
    setIdentity(value);
    setReason('');
    setIsError(false);
    setErrorText('');
  };

  const actionText =
    actionType === 'reschedule'
      ? t('Reschedule', 'Reschedule')
      : t('Cancel', 'Cancel');

  const formattedDate = formatDateTime(originallyScheduledDate, t, makeKey);

  return (
    <Card classNames={{wrapper: css.cardWrapper}}>
      <div className={css.promptWrapper}>
        <div className={css.promptHeader}>
          <div>
            <SemanticIcon semantic="information" name="calendar" size="large" />
          </div>
          <SubTitleLarge>
            {actionText}
            {` ${t('Meeting', 'Meeting')}`}
          </SubTitleLarge>
          <div className={css.scheduledTimeWrapper}>
            <BodySmall>
              {t('Meeting_scheduled_for', 'Meeting scheduled for')}
            </BodySmall>
            <BodySmallBold>{formattedDate}</BodySmallBold>
          </div>
        </div>
        <div className={css.promptContent}>
          <SubTitleMedium>
            {`${t('To', 'To')} `}
            {actionText.toLowerCase()}
            {` ${t(
              'meeting_please_confirm_your_identity',
              'meeting, please confirm your identity',
            )}`}
          </SubTitleMedium>
          <ClickableCard
            classNames={{wrapper: css.identityCard}}
            onClick={() =>
              identity !== 'CANDIDATE' && changeIdentity('CANDIDATE')
            }
          >
            <BodyMediumBold>
              {t('I_am', 'I am')}{' '}
              {event
                ? event.primary_guest_name
                : t('the_guest_of_this_meeting', 'the guest of this meeting')}
            </BodyMediumBold>
            <RadioButton
              value="CANDIDATE"
              name="CANDIDATE"
              selectedValue={identity ? identity : ''}
            />
          </ClickableCard>
          <ClickableCard
            classNames={{wrapper: css.identityCard}}
            onClick={() => identity !== 'HOST' && changeIdentity('HOST')}
          >
            <BodyMediumBold>{` ${t(
              'I_am_the_host_of_this_meeting',
              'I am the host of this meeting',
            )}`}</BodyMediumBold>
            <RadioButton
              value="HOST"
              name="HOST"
              selectedValue={identity ? identity : ''}
            />
          </ClickableCard>
          {identity &&
            (identity === 'CANDIDATE' ? (
              <CandidateIdentityAction
                isError={isError}
                errorText={errorText}
                actionText={actionText}
                loading={loading}
                requested={requested}
                handleSubmit={handleSubmit}
              />
            ) : (
              <HostIdentityAction
                actionType={actionType}
                reason={reason}
                setReason={setReason}
                requested={requested}
                isError={isError}
                errorText={errorText}
                loading={loading}
                handleSubmit={handleSubmit}
              />
            ))}
        </div>
      </div>
    </Card>
  );
};

export default ActionPrompt;

const CandidateIdentityAction = ({
  isError,
  errorText,
  actionText,
  loading,
  requested,
  handleSubmit,
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return isError ? (
    <Banner semantic="danger">
      {errorText || t('Something_went_wrong', 'Something went wrong')}
    </Banner>
  ) : requested ? (
    <Banner semantic="information">
      {`${t('Link_to', 'Link to')} ${actionText.toLowerCase()} ${t(
        'the_meeting_has_been_sent_to_your_email',
        'the meeting has been sent to your email',
      )}`}
    </Banner>
  ) : (
    <Button isLoading={loading} onClick={handleSubmit}>
      {`${t('Email_me', 'Email me')} `}
      {actionText}
      {` ${t('Link', 'Link')}`}
    </Button>
  );
};

const HostIdentityAction = ({
  actionType,
  reason,
  setReason,
  requested,
  isError,
  errorText,
  loading,
  handleSubmit,
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  return actionType === 'reschedule' ? (
    <div className={css.reasonWrapper}>
      <Textarea
        value={reason}
        onChange={(evt) => setReason(evt.target.value)}
        label={t('Reason_for_reschedule', 'Reason for reschedule')}
        placeholder={t('share_reason_here', 'share reason here')}
      />
      {requested ? (
        isError ? (
          <Banner semantic="danger">
            {errorText || t('Something_went_wrong', 'Something went wrong')}
          </Banner>
        ) : (
          <Banner semantic="success">
            {t(
              'Email_requesting_guest_to_Reschedule_meeting_has_been_sent',
              'Email requesting guest to Reschedule meeting has been sent',
            )}
          </Banner>
        )
      ) : (
        <Button isLoading={loading} disabled={!reason} onClick={handleSubmit}>
          {t('Request_Reschedule', 'Request Reschedule')}
        </Button>
      )}
    </div>
  ) : requested ? (
    isError ? (
      <Banner semantic="danger">
        {errorText || t('Something_went_wrong', 'Something went wrong')}
      </Banner>
    ) : (
      <Banner semantic="information">
        {t(
          'Link_to_cancel_the_meeting_has_been_sent_to_your_email',
          'Link to cancel the meeting has been sent to your email',
        )}
      </Banner>
    )
  ) : (
    <Button isLoading={loading} onClick={handleSubmit}>
      {t('Email_me_a_Cancel_Link', 'Email me a Cancel Link')}
    </Button>
  );
};
